import {
	AirlineFragment,
	AirportFragment,
	DurationFragment,
	FullFlightsScheduleFragment,
	Maybe,
	ScheduleAircraftFragment,
	ScheduleFlightDirectionsLegsFragment,
	ScheduleResultPointFragment,
	ScheduleResultSegmentFragment,
	ScheduleSegmentStopFragment
} from '@websky/graphql';

import { toMapById } from '@shared/lib/toMapById';

export type ResolvedSchedulePoint = Omit<ScheduleResultPointFragment, 'airport'> & {
	airport: AirportFragment;
};

export type ResolvedScheduleStop = Omit<ScheduleSegmentStopFragment, 'airport'> & {
	airport: AirportFragment;
};

export type ResolvedScheduleSegment = Omit<
	ScheduleResultSegmentFragment,
	'operatingAirline' | 'marketingAirline' | 'departure' | 'arrival' | 'stops' | 'aircraft' | 'aircrafts'
> & {
	operatingAirline: AirlineFragment;
	marketingAirline: AirlineFragment;
	departure: ResolvedSchedulePoint;
	arrival: ResolvedSchedulePoint;
	stops: Array<Maybe<ResolvedScheduleStop>>;
	aircraft?: Maybe<ScheduleAircraftFragment>;
	aircrafts?: Maybe<Array<Maybe<ScheduleAircraftFragment>>>;
};

export type ResolvedScheduleLeg = Omit<ScheduleFlightDirectionsLegsFragment, 'segments'> & {
	segments: Array<Maybe<{ segment: ResolvedScheduleSegment; transferDuration?: Maybe<DurationFragment> }>>;
};

export type ResolvedScheduleSearchParameters = {
	segments: Array<Maybe<{ departure: AirportFragment; arrival: AirportFragment }>>;
};

export type ResolvedSchedule = Omit<FullFlightsScheduleFragment, 'searchParameters' | 'flightDirections'> & {
	searchParameters: ResolvedScheduleSearchParameters;
	flightDirections: Array<Maybe<{ legs: Array<Maybe<ResolvedScheduleLeg>> }>>;
};

interface ScheduleReferences {
	airports: Map<string, AirportFragment>;
	airlines: Map<string, AirlineFragment>;
	aircrafts: Map<string, ScheduleAircraftFragment>;
}

const buildReferences = (schedule: FullFlightsScheduleFragment): ScheduleReferences => ({
	airports: toMapById(schedule.airports),
	airlines: toMapById(schedule.airlines),
	aircrafts: toMapById(schedule.aircrafts)
});

const resolvePoint = (
	point: ScheduleResultPointFragment,
	references: ScheduleReferences
): ResolvedSchedulePoint | null => {
	const airport = references.airports.get(point.airport?.id);

	return airport ? { ...point, airport } : null;
};

const resolveStop = (
	stop: ScheduleSegmentStopFragment,
	references: ScheduleReferences
): ResolvedScheduleStop | null => {
	const airport = references.airports.get(stop.airport?.id);

	return airport ? { ...stop, airport } : null;
};

const resolveSegment = (
	segment: ScheduleResultSegmentFragment,
	references: ScheduleReferences
): ResolvedScheduleSegment | null => {
	const operatingAirline = references.airlines.get(segment.operatingAirline?.id);
	const marketingAirline = references.airlines.get(segment.marketingAirline?.id);
	const departure = segment.departure ? resolvePoint(segment.departure, references) : null;
	const arrival = segment.arrival ? resolvePoint(segment.arrival, references) : null;

	if (!operatingAirline || !marketingAirline || !departure || !arrival) {
		return null;
	}

	const stops: Array<Maybe<ResolvedScheduleStop>> = [];

	for (const stop of segment.stops ?? []) {
		const resolvedStop = stop ? resolveStop(stop, references) : null;

		if (stop && !resolvedStop) {
			return null;
		}

		stops.push(resolvedStop);
	}

	return {
		...segment,
		operatingAirline,
		marketingAirline,
		departure,
		arrival,
		stops,
		aircraft: references.aircrafts.get(segment.aircraft?.id) ?? null,
		aircrafts: segment.aircrafts?.map(aircraft => references.aircrafts.get(aircraft?.id) ?? null)
	};
};

const resolveSearchParameters = (
	schedule: FullFlightsScheduleFragment,
	references: ScheduleReferences
): ResolvedScheduleSearchParameters => ({
	segments:
		schedule.searchParameters.segments?.map(segment => {
			if (!segment) {
				return null;
			}

			const departure = references.airports.get(segment.departure?.id) ?? segment.departure;
			const arrival = references.airports.get(segment.arrival?.id) ?? segment.arrival;

			return departure && arrival ? { departure, arrival } : null;
		}) ?? []
});

export const resolveScheduleReferences = (schedule: FullFlightsScheduleFragment): ResolvedSchedule => {
	const references = buildReferences(schedule);

	return {
		...schedule,
		searchParameters: resolveSearchParameters(schedule, references),
		flightDirections:
			schedule.flightDirections?.map(
				direction =>
					direction && {
						...direction,
						legs:
							direction.legs?.map(
								leg =>
									leg && {
										...leg,
										segments:
											leg.segments?.map(item => {
											if (!item) {
												return null;
											}

												const segment = resolveSegment(item.segment, references);

												return segment ? { ...item, segment } : null;
											}) ?? []
									}
							) ?? []
					}
			) ?? []
	};
};
